import PDFDocument from 'pdfkit/js/pdfkit.standalone.js';
import {
	cmToPoint,
	getPresaleTableColWidths,
	renderAddress,
	renderDetails,
	renderFooter,
	renderHeader,
	renderLine,
	renderPresaleTable,
	renderSummaryTable,
	renderTitle
} from './helpers/index';
import {
	COLOR_LINE,
	COL_HEIGHT,
	COL_TEXT_OPTIONS,
	COL_TEXT_SIZE,
	FONT,
	FONT_BOLD,
	FONT_SIZE_NORMAL,
	FONT_SIZE_SMALL,
	FOOTER_MARGIN,
	LINE_WEIGHT,
	MINIMUM_ROWS_PER_PAGE,
	SPACE
} from './variables';

export async function createPresalePdf(body: any, afterInit: (pdf: PDFDocument) => Promise<void>) {
	// create a document and pipe to a blob
	const pdf = new PDFDocument({
		bufferPages: true,
		layout: 'portrait',
		size: 'A4',
		margins: {
			top: cmToPoint(1.27),
			bottom: cmToPoint(1.27),
			left: cmToPoint(2),
			right: cmToPoint(1.27)
		}
	});

	await afterInit(pdf);

	await renderHeader(pdf, body);
	renderAddress(pdf, body);
	renderTitle(pdf, body);
	renderDetails(pdf, body);

	pdf.y += cmToPoint(SPACE);

	for (const car of body.cars) {
		renderCarAndReferenceTable(pdf, car.car, car.reference);

		for (const product of car.products) {
			renderPresaleTable(pdf, product.header, product.items);
		}
	}

	const { colDescription } = getPresaleTableColWidths(pdf);

	renderSummaryTable(pdf, body, colDescription);
	renderComments(pdf, body);
	renderFooter(pdf, body);

	return pdf;
}

export function renderCarAndReferenceTable(pdf: PDFDocument, car: string, reference?: string) {
	pdf.y += cmToPoint(0.1);

	renderLine(pdf, COLOR_LINE, LINE_WEIGHT);

	pdf.y += cmToPoint(0.1);

	// calculate size for total price
	pdf.fontSize(COL_TEXT_SIZE).font(FONT_BOLD);

	const maxY = pdf.page.height - pdf.page.margins.bottom;
	const width = pdf.page.width - pdf.page.margins.left - pdf.page.margins.right;

	const colText = Math.min(width, pdf.widthOfString(car, COL_TEXT_OPTIONS) + 10); // 10 = margin
	const colReference = width - colText;

	// it should be place enough for some rows
	if (pdf.y + COL_HEIGHT * MINIMUM_ROWS_PER_PAGE + FOOTER_MARGIN > maxY) {
		pdf.addPage();
	}

	const startX = pdf.x;
	const startY = pdf.y;
	const colX = startX;

	pdf.fontSize(COL_TEXT_SIZE).font(FONT_BOLD);

	pdf.text(car, colX, startY, {
		...COL_TEXT_OPTIONS,
		width: colText
	});

	pdf.fontSize(COL_TEXT_SIZE).font(FONT);

	pdf.text(reference || '', colX + colText, startY, {
		...COL_TEXT_OPTIONS,
		width: colReference,
		align: 'right'
	});

	pdf.x = startX;
	pdf.y = startY + COL_HEIGHT;
}

function renderComments(pdf: PDFDocument, body: any) {
	if (!body.comments?.value) {
		return;
	}

	pdf.y += cmToPoint(SPACE * 2);

	pdf.fontSize(FONT_SIZE_NORMAL).font(FONT);

	pdf.text(body.comments.label, pdf.x, pdf.y, {
		lineGap: 1.65
	});

	pdf.fontSize(FONT_SIZE_SMALL).font(FONT);

	pdf.text(body.comments.value, pdf.x, pdf.y, {
		lineGap: 1.65
	});
}
